/**
 * Toast Component
 *
 * Toast notification system with multiple variants.
 */

import { useEffect, useState } from 'react';
import { cn } from '@/utils';
import { Logger, LogCategory } from '@/utils/logger';
import { useToasts, useUIStore } from '@/stores/uiStore';
import {
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Info,
  X,
} from 'lucide-react';
import type { Toast as ToastType, ToastType as ToastVariant } from '@/types';

// ============================================================================
// Icons
// ============================================================================

const toastIcons: Record<ToastVariant, React.ReactNode> = {
  success: <CheckCircle2 className="h-5 w-5" />,
  error: <AlertCircle className="h-5 w-5" />,
  warning: <AlertTriangle className="h-5 w-5" />,
  info: <Info className="h-5 w-5" />,
};

// ============================================================================
// Styles
// ============================================================================

const toastStyles: Record<ToastVariant, string> = {
  success: 'bg-success-50 border-success-200 text-success-800',
  error: 'bg-danger-50 border-danger-200 text-danger-800',
  warning: 'bg-warning-50 border-warning-200 text-warning-800',
  info: 'bg-primary-50 border-primary-200 text-primary-800',
};

const iconStyles: Record<ToastVariant, string> = {
  success: 'text-success-500',
  error: 'text-danger-500',
  warning: 'text-warning-500',
  info: 'text-primary-500',
};

// ============================================================================
// Toast Item Component
// ============================================================================

interface ToastItemProps {
  toast: ToastType;
  onDismiss: (id: string) => void;
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    // Trigger enter animation
    const showTimer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(showTimer);
  }, []);

  const handleDismiss = () => {
    Logger.debug(LogCategory.UI, `Dismissing toast: ${toast.id}`, 'ToastItem');
    setIsLeaving(true);
    setTimeout(() => onDismiss(toast.id), 200);
  };

  return (
    <div
      role="alert"
      aria-live="polite"
      className={cn(
        'flex items-start gap-3 p-4 rounded-lg border shadow-lg',
        'transition-all duration-200 ease-out',
        toastStyles[toast.type],
        isVisible && !isLeaving ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
      )}
    >
      <span className={cn('shrink-0 mt-0.5', iconStyles[toast.type])}>
        {toastIcons[toast.type]}
      </span>

      <div className="flex-1 min-w-0">
        <p className="font-medium">{toast.title}</p>
        {toast.message && (
          <p className="mt-1 text-sm opacity-90">{toast.message}</p>
        )}
        {toast.action && (
          <button
            onClick={() => {
              Logger.debug(LogCategory.UI, 'Toast action clicked', 'ToastItem', {
                toastId: toast.id,
              });
              toast.action?.onClick();
              handleDismiss();
            }}
            className="mt-2 text-sm font-medium underline hover:no-underline"
          >
            {toast.action.label}
          </button>
        )}
      </div>

      <button
        onClick={handleDismiss}
        className="shrink-0 p-1 -mr-1 -mt-1 rounded hover:bg-black/5 transition-colors"
        aria-label="Dismiss notification"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

// ============================================================================
// Toast Container Component
// ============================================================================

export function ToastContainer() {
  const toasts = useToasts();
  const removeToast = useUIStore((state) => state.removeToast);

  if (toasts.length === 0) return null;

  return (
    <div
      aria-label="Notifications"
      className="fixed top-4 right-4 z-50 flex flex-col gap-2 w-full max-w-sm"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={removeToast} />
      ))}
    </div>
  );
}

// ============================================================================
// useToast Hook (for easier toast creation)
// ============================================================================

export function useToast() {
  const store = useUIStore();

  return {
    success: (title: string, message?: string) => {
      Logger.info(LogCategory.UI, `Success toast: ${title}`, 'useToast');
      return store.addToast({ type: 'success', title, message });
    },
    error: (title: string, message?: string) => {
      Logger.warn(LogCategory.UI, `Error toast: ${title}`, 'useToast');
      return store.addToast({ type: 'error', title, message, duration: 8000 });
    },
    warning: (title: string, message?: string) => {
      Logger.info(LogCategory.UI, `Warning toast: ${title}`, 'useToast');
      return store.addToast({ type: 'warning', title, message });
    },
    info: (title: string, message?: string) => {
      Logger.debug(LogCategory.UI, `Info toast: ${title}`, 'useToast');
      return store.addToast({ type: 'info', title, message });
    },
    dismiss: (id: string) => {
      store.removeToast(id);
    },
    dismissAll: () => {
      store.clearToasts();
    },
  };
}

// ============================================================================
// Toast Provider Component
// ============================================================================

interface ToastProviderProps {
  children: React.ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  return (
    <>
      {children}
      <ToastContainer />
    </>
  );
}
